import React from "react";
import { Input, Row, Col, Select, Tooltip } from "antd";
import { SettingOutlined, SearchOutlined, PrinterOutlined } from "@ant-design/icons";

import "./SearchWidget.css";
import CommonButton from "components/ButtonWidgets/CommonButton/CommonButton";
import ExportExcelWidget from "components/ExportExcelWidget/ExportExcelWidget";
import { useEffect } from "react";
import { useState } from "react";
import { getColumnConfig } from "../RegFormTable/columnConfig";
const { Search } = Input;
const { Option } = Select;

// Per-field display transforms for export — applied on top of the raw value
// when a column needs something other than a plain pass-through (e.g. 0 -> "K"
// for grade). Anything not listed here is exported as-is (null/undefined -> "").
const EXPORT_VALUE_TRANSFORMS = {
  camper_grade: (v) => (v == 0 ? "K" : v),
  grade: (v) => (v == "0" ? "K" : v),
  immun_verified: (v) => (v === 1 || v === "1" || v === true || v === "true" ? "Yes" : "No"),
};

// Builds one export row for the Participants report (and its group-by drill-down,
// which shares the exact same row shape), covering every column currently visible
// in the Column Picker for this sport type — so a newly added/visible column is
// included automatically without a second place to remember to update.
const buildParticipantExportRow = (x, sport_type, columnPreferences) => {
  const columnConfig = getColumnConfig(sport_type);
  const prefs = columnPreferences?.columns || {};
  const row = {};

  Object.keys(columnConfig).forEach((key) => {
    if (key === "action") return; // UI-only column, not real data
    const config = columnConfig[key];
    const pref = prefs[key];
    const visible = pref ? pref.visible : config.defaultVisible;
    if (!visible && !config.locked) return;

    const rawValue = x[key];
    const transform = EXPORT_VALUE_TRANSFORMS[key];
    row[config.label] = transform ? transform(rawValue) : (rawValue ?? "");
  });

  return row;
};

function SearchWidget(props) {
  const {
    dataList,
    groupList,
    isGroup,
    searchKey,
    setSearchKey,
    onSearch,
    sport_type,
    onColumnSettings,
    onReportChange,
    currentReport,
    onPrint,
    registrationType,
    facilityOptions,
    facilityFilter,
    onFacilityFilter,
    columnPreferences,
  } = props;

  const isFacility = (registrationType || "").toLowerCase() === "facility";

  const [excelData, setExcelData] = useState([]);

  useEffect(() => {
    // When in group view, export the group summary list
    if (isGroup && groupList.length > 0) {
      const tmpList = groupList.map((x) => ({
        Label: x.label_name,
        "Count": x.reg_count,
        "Total": x.total_reg,
      }));
      setExcelData(tmpList);
      return;
    }

    if (dataList.length > 0) {
      let tmpList = [];

      // Handle Registration Totals report
      if (currentReport === "registration_totals") {
        dataList.map((x) => {
          let tmpObj = {
            Type: x.type === "registration" ? "Registration" : "Program",
            Name: x.name,
            Count: x.count || 0,
            "Total Amount": x.total_amount || 0,
            "Last Timestamp": x.last_timestamp || "N/A",
          };
          tmpList = [...tmpList, tmpObj];
        });
      }
      // Handle Registrations (transactions) report
      else if (currentReport === "registrations" && !isFacility) {
        dataList.map((x) => {
          let tmpObj = {
            "Parent First Name": x.parent_first,
            "Parent Last Name": x.parent_last,
            "Parent Email": x.p_email,
            "Phone": x.p_cell_phone,
            "Total Checkout": x.total_checkout,
            "Payment Method": x.payment_method,
            "Discount Code": x.discount_code,
            "Discount Amount": x.discount_amount,
            "Registration Date": x.reg_date,
            "Participant Count": x.participant_count,
            "Program Count": x.program_count,
          };
          tmpList = [...tmpList, tmpObj];
        });
      }
      // Handle Facility Registrations (paid) + Unpaid for facility
      else if (isFacility && (currentReport === "registrations" || currentReport === "unpaid")) {
        dataList.map((x) => {
          let tmpObj = {
            "ID": x.id,
            "Applicant Name": x.applicant_name,
            "Facility": x.facility,
            "Event Date": x.event_date,
            "Start Time": x.event_start_time,
            "End Time": x.event_end_time,
            "Total Hours": x.total_hours,
            "Amount": x.total_amount,
            "Email": x.contact_email,
            "Phone": x.contact_phone,
            "Status": x.status,
            "Submitted": x.created_at,
          };
          tmpList = [...tmpList, tmpObj];
        });
      }
      // Handle Participants report, and its Group-by drill-downs (discount_code,
      // reg_date, payment_method, group_program) — after clicking a group row,
      // isGroup=false but currentReport stays as the group type, and dataList
      // (userList) is the same participant-record shape as the Participants report.
      // Export every column currently visible in the Column Picker for this sport
      // type, so a newly added/visible column is included automatically.
      else if (
        currentReport === "participants" ||
        ["discount_code", "reg_date", "payment_method", "group_program"].includes(currentReport)
      ) {
        dataList.map((x) => {
          tmpList = [...tmpList, buildParticipantExportRow(x, sport_type, columnPreferences)];
        });
      }
      // Handle Facility Applications report
      else if (currentReport === "facility_applications") {
        dataList.map((x) => {
          let tmpObj = {
            "ID": x.id,
            "Applicant Name": x.applicant_name,
            "Facility": x.facility,
            "Event Date": x.event_date,
            "Start Time": x.event_start_time,
            "End Time": x.event_end_time,
            "Total Hours": x.total_hours,
            "Amount": x.total_amount,
            "Email": x.contact_email,
            "Phone": x.contact_phone,
            "Status": x.status,
            "Submitted": x.created_at,
          };
          tmpList = [...tmpList, tmpObj];
        });
      }
      setExcelData(tmpList);
    } else {
      setExcelData([]);
    }
  }, [dataList, groupList, isGroup, currentReport, sport_type, isFacility, columnPreferences]);

  return (
    <div>
      {/* Report Type, Search, and Action Buttons - all in one row */}
      <Row className="follower-cnt-txt" align="middle" justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <Row gutter={16} align="middle">
            <Col>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 400, fontSize: 14, whiteSpace: 'nowrap' }}>
                  Report Type:
                </label>
                <Select
                  className="common-select report-type-select"
                  placeholder="Select Report"
                  style={{ width: 360 }}
                  value={currentReport || (isFacility ? "facility_applications" : "participants")}
                  onChange={(v) => {
                    onReportChange(v);
                  }}
                >
                  {isFacility ? (
                    <>
                      <Option value="facility_applications">Facility Applications</Option>
                      <Option value="facility_calendar">Facility Calendar</Option>
                      <Option value="registrations">Transactions</Option>
                      <Option value="unpaid">Unpaid</Option>
                    </>
                  ) : (
                    <>
                      <Option value="participants">Participants</Option>
                      <Option value="registrations">Registrations</Option>
                      <Option value="registration_totals">Registration Totals</Option>
                      <Option value="discount_code">Group by Discount Code</Option>
                      <Option value="reg_date">Group by Registration Date</Option>
                      <Option value="payment_method">Group by Payment Method</Option>
                      <Option value="group_program">Group by Program</Option>
                      <Option value="unpaid">Unpaid Registrations</Option>
                    </>
                  )}
                </Select>
              </div>
            </Col>
            {isFacility && (
              <Col>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 400, fontSize: 14, whiteSpace: 'nowrap' }}>
                    Facility:
                  </label>
                  <Select
                    className="common-select report-type-select"
                    style={{ width: 240 }}
                    value={facilityFilter || "all"}
                    onChange={(v) => onFacilityFilter && onFacilityFilter(v)}
                  >
                    <Option value="all">All Facilities</Option>
                    {(facilityOptions || []).map((facility) => (
                      <Option key={facility} value={facility}>
                        {facility}
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
            )}
            <Col>
              <span style={{ marginLeft: 16 }}>Count: {isGroup ? groupList.length : dataList.length}</span>
            </Col>
          </Row>
        </Col>
        <Col>
          <Row gutter={16} align="middle">
            <Col>
              <Search
                placeholder={isGroup ? "Search label" : "Search name or email"}
                allowClear
                onSearch={(v) => {
                  onSearch(v);
                }}
                onChange={(e) => {
                  setSearchKey(e.target.value);
                }}
                value={searchKey}
                enterButton={<SearchOutlined />}
              />
            </Col>
            <Col>
              <ExportExcelWidget
                excelData={excelData}
                fileName={"registration_users"}
                onlyColumnsWithValues={true}
              />
            </Col>
            {currentReport === "registration_totals" ? (
              <Col>
                <Tooltip title="Print this report">
                  <CommonButton onClick={onPrint}>
                    <PrinterOutlined style={{ marginRight: 6 }} />
                    Print
                  </CommonButton>
                </Tooltip>
              </Col>
            ) : onColumnSettings ? (
              <Col>
                <Tooltip title="Customize table columns">
                  <CommonButton
                    onClick={onColumnSettings}
                  >
                    <SettingOutlined style={{ marginRight: 6 }} />
                    Columns
                  </CommonButton>
                </Tooltip>
              </Col>
            ) : null}
          </Row>
        </Col>
      </Row>
    </div>
  );
}

export default SearchWidget;
